/**
 * Login corporativo pelo próprio Word (SSO do Office / Microsoft Entra ID).
 *
 * Quem emite o token é o host: `OfficeRuntime.auth.getAccessToken()` devolve um
 * JWT do usuário já logado no Office, sem tela de login, sem popup, sem
 * biblioteca de autenticação. O add-in só repassa esse token em
 * `Authorization: Bearer` — quem decide se ele vale é o backend, que confere a
 * assinatura contra as chaves do tenant (backend/app/auth/entra.py).
 *
 * O que o painel NÃO faz aqui, de propósito:
 *  - não guarda o token em localStorage. Ele vale ~1h e o Office reemite de
 *    graça; persistir só criaria uma cópia de credencial no disco do usuário.
 *  - não decodifica o token para descobrir o e-mail. O e-mail que vale é o que
 *    o backend devolve em `GET /me`, depois de validar. Ler a claim aqui daria
 *    um valor que parece igual e não tem garantia nenhuma por trás.
 */
import { dlog } from './log';
/** Erro de login com uma frase que o usuário consegue agir em cima. */
export class AuthError extends Error {
    constructor(message, 
    /** Código do Office (13xxx) quando houve um; útil no log e no suporte. */
    code, 
    /** O usuário pode resolver clicando "Tentar de novo"? */
    retryable = true, 
    /**
     * Quanto esperar antes de tentar de novo faz sentido, em ms. Só o
     * throttling (13013) preenche: clicar de novo na hora PIORA o problema, e
     * a tela usa isto para desabilitar o botão durante a espera.
     */
    retryAfterMs = 0) {
        super(message);
        this.code = code;
        this.retryable = retryable;
        this.retryAfterMs = retryAfterMs;
        this.name = 'AuthError';
    }
}
/**
 * Códigos TRANSITÓRIOS: a identidade está certa, o host só não pode responder
 * agora. Errar isto é caro — tratar throttling como "credencial inválida"
 * transforma uma espera de segundos numa tela de bloqueio permanente.
 */
const THROTTLED = 13013; // "chamou getAccessToken vezes demais em pouco tempo"
const TRANSIENT_CODES = new Set([THROTTLED, 13012]);
/** Backoff entre as tentativas internas de um throttle. */
const THROTTLE_BACKOFF_MS = [1000, 3000, 7000];
const espera = (ms) => new Promise((r) => setTimeout(r, ms));
/**
 * Tradução dos códigos do `getAccessToken`.
 *
 * Só entram aqui os que pedem AÇÕES DIFERENTES do usuário — o resto cai na
 * frase genérica. Um erro de SSO já é confuso; multiplicar mensagens quase
 * iguais não ajudaria ninguém a sair do lugar.
 */
function describe(code, fallback) {
    switch (code) {
        case 13000:
        case 13010:
            return new AuthError('Esta versão do Word não consegue fazer o login corporativo. ' +
                'Atualize o Office ou abra o documento no Word para desktop.', code, false);
        case 13001:
            return new AuthError('Você não está conectado ao Office com a conta da empresa. ' +
                'Entre no Word com a sua conta corporativa e recarregue o painel.', code);
        case 13002:
            return new AuthError('O acesso não foi autorizado. Toque em "Tentar de novo" e aceite a ' +
                'permissão que o Office pedir.', code);
        case 13003:
            return new AuthError('Esta conta não é corporativa. O mIAu só funciona com a conta ' +
                'Microsoft da empresa.', code, false);
        case 13005:
        case 13007:
            // Consentimento do administrador ausente, ou registro do aplicativo
            // incompleto: nenhuma das duas o usuário resolve sozinho.
            return new AuthError('O mIAu ainda não foi liberado para a sua conta. ' +
                'Avise quem administra o mIAu na empresa.', code, false);
        case 13012:
            return new AuthError('Não foi possível verificar a sua conta agora. Verifique a conexão ' +
                'de rede e tente de novo.', code);
        case THROTTLED:
            // O Office limita quantas vezes o add-in pode pedir token num intervalo
            // curto. Abrir e fechar o painel várias vezes seguidas chega lá — e a
            // reação natural do usuário (clicar "Tentar de novo") só afunda mais.
            // Por isso a mensagem manda ESPERAR, e o botão fica travado até passar.
            return new AuthError('Fiz muitas verificações seguidas e o Office pediu uma pausa. ' +
                'Aguarde alguns segundos — eu tento de novo sozinho.', code, true, 15000);
        default:
            return new AuthError(fallback, code);
    }
}
/**
 * Token em memória. Guardamos a hora de expiração para não pedir um token novo
 * a cada requisição: o `getAccessToken` do Office tem cache próprio, mas passar
 * por ele em todo `fetch` custa um round-trip com o host a cada chamada.
 *
 * A margem de 5 minutos evita o caso chato: token que passa na checagem daqui e
 * expira no caminho até o backend.
 */
let cached = null;
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;
/** Instante de expiração do JWT (claim `exp`), ou 0 se não der para ler. */
function expiryOf(token) {
    try {
        const payload = token.split('.')[1];
        // base64url -> base64; `atob` não aceita '-' e '_'.
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        const exp = JSON.parse(json).exp;
        return typeof exp === 'number' ? exp * 1000 : 0;
    }
    catch (_a) {
        return 0;
    }
}
/** O host tem a API de SSO? (Word antigo, ou painel aberto fora do Office.) */
export function ssoAvailable() {
    var _a;
    const rt = globalThis
        .OfficeRuntime;
    return typeof ((_a = rt === null || rt === void 0 ? void 0 : rt.auth) === null || _a === void 0 ? void 0 : _a.getAccessToken) === 'function';
}
/** Token em cache ainda utilizável, ou `null`. Nunca chama o Office. */
function tokenEmCache() {
    if (cached && Date.now() < cached.expiresAt - EXPIRY_MARGIN_MS)
        return cached.token;
    return null;
}
/**
 * Pedido em voo. Duas chamadas simultâneas (o boot e a telemetria, por
 * exemplo) compartilham a MESMA promessa em vez de baterem duas vezes no host
 * — chamadas concorrentes contam duas vezes no throttle e ainda podem devolver
 * 13008 ("outra chamada em andamento").
 */
let emVoo = null;
/** Uma tentativa só, sem cache nem backoff. */
async function pedirAoOffice() {
    const auth = globalThis.OfficeRuntime.auth;
    try {
        const token = await auth.getAccessToken({
            allowSignInPrompt: true,
            allowConsentPrompt: true,
            // Não pedimos acesso ao Microsoft Graph: o mIAu só precisa saber QUEM é o
            // usuário. Pedir Graph exigiria consentimento para escopos que não usamos.
            forMSGraphAccess: false,
        });
        cached = { token, expiresAt: expiryOf(token) || Date.now() + 30 * 60 * 1000 };
        dlog('[auth] token obtido');
        return token;
    }
    catch (e) {
        const err = e;
        dlog('[auth] getAccessToken falhou', err === null || err === void 0 ? void 0 : err.code, err === null || err === void 0 ? void 0 : err.message);
        // O cache NÃO é limpo aqui: num erro transitório o token anterior continua
        // válido, e jogá-lo fora era o que obrigava a próxima chamada a incomodar o
        // Office de novo, bem no momento em que ele pediu pausa.
        throw describe(typeof (err === null || err === void 0 ? void 0 : err.code) === 'number' ? err.code : undefined, 'Não consegui verificar a sua conta corporativa. Tente de novo.');
    }
}
/**
 * Pede o token ao Office. `allowSignInPrompt` deixa o host abrir o fluxo de
 * login quando ninguém está conectado — é o que transforma "não está logado"
 * numa tela de login em vez de um erro seco.
 *
 * Erro transitório (throttling) é reengolido aqui, com backoff, em vez de subir
 * para a UI: é uma espera de segundos, não uma credencial inválida, e tratá-lo
 * como falha de login virava tela de bloqueio permanente.
 */
export async function acquireToken(force = false) {
    if (!force) {
        const emCache = tokenEmCache();
        if (emCache)
            return emCache;
    }
    if (!ssoAvailable()) {
        throw new AuthError('Esta versão do Word não oferece o login corporativo. Atualize o Office.', 13000, false);
    }
    if (emVoo)
        return emVoo;
    emVoo = (async () => {
        for (let tentativa = 0;; tentativa++) {
            try {
                return await pedirAoOffice();
            }
            catch (e) {
                const erro = e;
                const transitorio = erro.code !== undefined && TRANSIENT_CODES.has(erro.code);
                if (!transitorio || tentativa >= THROTTLE_BACKOFF_MS.length)
                    throw erro;
                dlog(`[auth] ${erro.code} transitório; nova tentativa em ${THROTTLE_BACKOFF_MS[tentativa]}ms`);
                await espera(THROTTLE_BACKOFF_MS[tentativa]);
            }
        }
    })();
    // `try/finally` de sintaxe (não `Promise.finally`, que exigiria lib es2018):
    // o pedido em voo tem que ser liberado mesmo quando a aquisição falha.
    try {
        return await emVoo;
    }
    finally {
        emVoo = null;
    }
}
/** Descarta o token em memória (usado quando o backend responde 401). */
export function forgetToken() {
    cached = null;
}
/**
 * Cabeçalho `Authorization` das chamadas ao BACKEND. Pode buscar um token novo.
 */
export async function authHeader() {
    if (!ssoAvailable())
        return {};
    try {
        return { Authorization: `Bearer ${await acquireToken()}` };
    }
    catch (_a) {
        // Em dev (AUTH_REQUIRED=false) o backend aceita sem token; em produção ele
        // devolve 401 e o painel mostra a tela de login. Falhar aqui em silêncio é
        // o que mantém os dois cenários no mesmo caminho de código.
        return {};
    }
}
/**
 * Cabeçalho para a TELEMETRIA: usa o token que já existe e NUNCA pede um novo.
 *
 * Esta separação conserta um ciclo real: um login que falha emite um log; o log
 * pedia token; esse pedido batia no host de novo, e no caso de throttling isso
 * renovava a punição que causou a falha. O log passava a alimentar o próprio
 * erro. Telemetria é observação — não pode ter efeito sobre o que observa.
 */
export function cachedAuthHeader() {
    const token = tokenEmCache();
    return token ? { Authorization: `Bearer ${token}` } : {};
}
